import React, { useState, useEffect, useRef } from 'react';

const FilterModal = ({ isOpen, onClose, cookbooks, categories, ingredients, appliedCookbookIds, appliedCategoryIds, appliedIngredientIds, onApplyFilter }) => {
  const [selectedCookbookIds, setSelectedCookbookIds] = useState(appliedCookbookIds);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState(appliedCategoryIds);
  const [selectedIngredientIds, setSelectedIngredientIds] = useState(appliedIngredientIds);
  const [cookbookSearchTerm, setCookbookSearchTerm] = useState('');
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [ingredientSearchTerm, setIngredientSearchTerm] = useState('');
  const [isCookbookDropdownOpen, setIsCookbookDropdownOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isIngredientDropdownOpen, setIsIngredientDropdownOpen] = useState(false);
  const modalRef = useRef(null);
  const categoryInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        const hasFilters = appliedCookbookIds.length > 0 || appliedCategoryIds.length > 0 || appliedIngredientIds.length > 0;
        if (!hasFilters && categoryInputRef.current) {
          categoryInputRef.current.focus();
        }
      }, 0);
    }
  }, [isOpen, appliedCookbookIds, appliedCategoryIds, appliedIngredientIds]);

  useEffect(() => {
    if (isOpen) {
      setSelectedCookbookIds(appliedCookbookIds);
      setSelectedCategoryIds(appliedCategoryIds);
      setSelectedIngredientIds(appliedIngredientIds);
    }
  }, [isOpen, appliedCookbookIds, appliedCategoryIds, appliedIngredientIds]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'Tab' && modalRef.current) {
        const focusableElements = Array.from(modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )).filter(el => el.offsetParent !== null);

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const currentIndex = focusableElements.indexOf(document.activeElement);

        event.preventDefault();

        if (event.shiftKey) {
          if (currentIndex <= 0) {
            lastElement.focus();
          } else {
            focusableElements[currentIndex - 1].focus();
          }
        } else {
          if (currentIndex === -1 || currentIndex === focusableElements.length - 1) {
            firstElement.focus();
          } else {
            focusableElements[currentIndex + 1].focus();
          }
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, selectedCookbookIds, selectedCategoryIds, selectedIngredientIds, onClose]);

  if (!isOpen) return null;

  const handleCookbookSelect = (cookbookId, operation = 'OR') => {
    const existingIndex = selectedCookbookIds.findIndex(item => (typeof item === 'object' ? item.id : item) === cookbookId);

    if (existingIndex >= 0) {
      const existingItem = selectedCookbookIds[existingIndex];
      const existingOp = typeof existingItem === 'object' ? existingItem.operation : 'OR';

      if (existingOp === operation) {
        setSelectedCookbookIds(selectedCookbookIds.filter((_, index) => index !== existingIndex));
      } else {
        const newSelection = [...selectedCookbookIds];
        newSelection[existingIndex] = { id: cookbookId, operation };
        setSelectedCookbookIds(newSelection);
      }
    } else {
      setSelectedCookbookIds([...selectedCookbookIds, { id: cookbookId, operation }]);
    }
    setCookbookSearchTerm('');
    setIsCookbookDropdownOpen(false);
  };

  const getOperationIcon = (operation) => {
    switch (operation) {
      case 'AND': return 'add_circle';
      case 'OR': return 'call_split'; // or 'layers', 'dns'
      case 'NOT': return 'remove_circle'; // or 'block'
      default: return 'help';
    }
  };

  const getOperationColor = (operation) => {
    switch (operation) {
      case 'AND': return '#4CAF50';
      case 'OR': return '#2196F3';
      case 'NOT': return '#f44336';
      default: return '#ccc';
    }
  };

  const handleCategorySelect = (categoryId, operation = 'OR') => {
    // Check if ID exists (checking both string IDs and object.ids)
    const existingIndex = selectedCategoryIds.findIndex(item => (typeof item === 'object' ? item.id : item) === categoryId);

    if (existingIndex >= 0) {
      const existingItem = selectedCategoryIds[existingIndex];
      const existingOp = typeof existingItem === 'object' ? existingItem.operation : 'OR';

      if (existingOp === operation) {
        // Toggle off if same operation
        setSelectedCategoryIds(selectedCategoryIds.filter((_, index) => index !== existingIndex));
      } else {
        // Update operation
        const newSelection = [...selectedCategoryIds];
        newSelection[existingIndex] = { id: categoryId, operation };
        setSelectedCategoryIds(newSelection);
      }
    } else {
      // Add new
      setSelectedCategoryIds([...selectedCategoryIds, { id: categoryId, operation }]);
    }
    setCategorySearchTerm('');
    // Keep dropdown open for multiple selections interactions? 
    // Usually clicking an item closes it, but clicking a specific operator might mean we want to stay?
    // Let's close it for now to be simple, or keep it open if interaction is complex.
    // Sidebar keeps list visible. Dropdown usually closes on selection.
    // If we have buttons, clicking a button should probably close it?
    // Let's close it.
    setIsCategoryDropdownOpen(false);
  };

  const handleIngredientSelect = (ingredientId, operation = 'OR') => {
    const existingIndex = selectedIngredientIds.findIndex(item => (typeof item === 'object' ? item.id : item) === ingredientId);

    if (existingIndex >= 0) {
      const existingItem = selectedIngredientIds[existingIndex];
      const existingOp = typeof existingItem === 'object' ? existingItem.operation : 'OR';

      if (existingOp === operation) {
        setSelectedIngredientIds(selectedIngredientIds.filter((_, index) => index !== existingIndex));
      } else {
        const newSelection = [...selectedIngredientIds];
        newSelection[existingIndex] = { id: ingredientId, operation };
        setSelectedIngredientIds(newSelection);
      }
    } else {
      setSelectedIngredientIds([...selectedIngredientIds, { id: ingredientId, operation }]);
    }
    setIngredientSearchTerm('');
    setIsIngredientDropdownOpen(false);
  };

  const handleRemoveCookbook = (cookbookId) => {
    setSelectedCookbookIds(selectedCookbookIds.filter(item => (typeof item === 'object' ? item.id : item) !== cookbookId));
  };

  const handleRemoveCategory = (categoryId) => {
    setSelectedCategoryIds(selectedCategoryIds.filter(item => (typeof item === 'object' ? item.id : item) !== categoryId));
  };

  const handleRemoveIngredient = (ingredientId) => {
    setSelectedIngredientIds(selectedIngredientIds.filter(item => (typeof item === 'object' ? item.id : item) !== ingredientId));
  };

  const handleApply = () => {
    onApplyFilter(selectedCookbookIds, selectedCategoryIds, selectedIngredientIds);
    onClose();
  };

  const filteredCookbooks = cookbooks
    .filter(cookbook =>
      cookbook.title.toLowerCase().includes(cookbookSearchTerm.toLowerCase())
    )
    .sort((a, b) => a.title.localeCompare(b.title));

  const filteredCategories = categories
    .filter(category =>
      category.title.toLowerCase().includes(categorySearchTerm.toLowerCase())
    )
    .sort((a, b) => a.title.localeCompare(b.title));

  const filteredIngredients = ingredients
    .filter(ingredient =>
      ingredient.name.toLowerCase().includes(ingredientSearchTerm.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="modal-overlay">
      <div className="modal-content filter-modal-container" ref={modalRef}>
        <div className="modal-header">
          <h2>Filter Recipes</h2>
          <button onClick={onClose} className="icon-button close-button" title="Close"><i className="material-icons">close</i></button>
        </div>
        <div className="modal-body filter-modal-content">
          <div className="filter-container">
            <div className="filter-section">
              <div className="searchable-dropdown" onBlur={() => setIsCookbookDropdownOpen(false)}>
                <input
                  type="text"
                  placeholder="Filter by cookbooks..."
                  value={cookbookSearchTerm}
                  onChange={e => {
                    setCookbookSearchTerm(e.target.value);
                    setIsCookbookDropdownOpen(true);
                  }}
                  onFocus={() => setIsCookbookDropdownOpen(true)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && filteredCookbooks.length > 0) {
                      handleCookbookSelect(filteredCookbooks[0].id);
                    }
                  }}
                />
                {isCookbookDropdownOpen && (
                  <ul className="dropdown-list">
                    {filteredCookbooks.map(cookbook => {
                      const activeFilter = selectedCookbookIds.find(item => (typeof item === 'object' ? item.id : item) === cookbook.id);
                      const currentOp = activeFilter && typeof activeFilter === 'object' ? activeFilter.operation : (activeFilter ? 'OR' : null);

                      return (
                        <li key={cookbook.id} className="dropdown-item-with-actions">
                          <span onMouseDown={() => handleCookbookSelect(cookbook.id)} style={{ flexGrow: 1 }}>{cookbook.title}</span>
                          <div className="dropdown-item-actions">
                            <button onMouseDown={(e) => { e.stopPropagation(); handleCookbookSelect(cookbook.id, 'OR'); }}
                              className={`icon-button small ${currentOp === 'OR' ? 'active-blue' : ''}`} title="OR">
                              <i className="material-icons">call_split</i>
                            </button>
                            <button onMouseDown={(e) => { e.stopPropagation(); handleCookbookSelect(cookbook.id, 'NOT'); }}
                              className={`icon-button small ${currentOp === 'NOT' ? 'active-red' : ''}`} title="NOT">
                              <i className="material-icons">remove_circle</i>
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="selected-items">
                {selectedCookbookIds.map(item => {
                  const id = typeof item === 'object' && item !== null ? item.id : item;
                  const operation = typeof item === 'object' && item !== null ? item.operation : 'OR';
                  const cookbook = cookbooks.find(c => c.id === id);
                  if (!cookbook) return null;
                  return (
                    <div key={id} className="selected-item" style={{ backgroundColor: getOperationColor(operation) }}>
                      <span style={{ fontWeight: 'bold', marginRight: '5px' }}>{operation}:</span>
                      {cookbook.title}
                      <button title="Remove cookbook" onClick={() => handleRemoveCookbook(id)} className="remove-item-button"><i className="material-icons">close</i></button>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="filter-section">
              <div className="searchable-dropdown" onBlur={() => setIsCategoryDropdownOpen(false)}>
                <input
                  ref={categoryInputRef}
                  type="text"
                  placeholder="Filter by categories..."
                  value={categorySearchTerm}
                  onChange={e => {
                    setCategorySearchTerm(e.target.value);
                    setIsCategoryDropdownOpen(true);
                  }}
                  onFocus={() => setIsCategoryDropdownOpen(true)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && filteredCategories.length > 0) {
                      handleCategorySelect(filteredCategories[0].id);
                    }
                  }}
                />
                {isCategoryDropdownOpen && (
                  <ul className="dropdown-list">
                    {filteredCategories.map(category => {
                      const activeFilter = selectedCategoryIds.find(item => (typeof item === 'object' ? item.id : item) === category.id);
                      const currentOp = activeFilter && typeof activeFilter === 'object' ? activeFilter.operation : (activeFilter ? 'OR' : null);

                      return (
                        <li key={category.id} className="dropdown-item-with-actions">
                          <span onMouseDown={() => handleCategorySelect(category.id)} style={{ flexGrow: 1 }}>{category.title}</span>
                          <div className="dropdown-item-actions">
                            <button onMouseDown={(e) => { e.stopPropagation(); handleCategorySelect(category.id, 'AND'); }}
                              className={`icon-button small ${currentOp === 'AND' ? 'active-green' : ''}`} title="AND">
                              <i className="material-icons">add_circle</i>
                            </button>
                            <button onMouseDown={(e) => { e.stopPropagation(); handleCategorySelect(category.id, 'OR'); }}
                              className={`icon-button small ${currentOp === 'OR' ? 'active-blue' : ''}`} title="OR">
                              <i className="material-icons">call_split</i>
                            </button>
                            <button onMouseDown={(e) => { e.stopPropagation(); handleCategorySelect(category.id, 'NOT'); }}
                              className={`icon-button small ${currentOp === 'NOT' ? 'active-red' : ''}`} title="NOT">
                              <i className="material-icons">remove_circle</i>
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="selected-items">
                {selectedCategoryIds.map(item => {
                  const id = typeof item === 'object' && item !== null ? item.id : item; // Ensure item is object and not null
                  const operation = typeof item === 'object' && item !== null ? item.operation : 'OR';
                  const category = categories.find(c => c.id === id);
                  if (!category) return null;
                  return (
                    <div key={id} className="selected-item" style={{ backgroundColor: getOperationColor(operation) }}>
                      <span style={{ fontWeight: 'bold', marginRight: '5px' }}>{operation}:</span>
                      {category.title}
                      <button title="Remove category" onClick={() => handleRemoveCategory(id)} className="remove-item-button"><i className="material-icons">close</i></button>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="filter-section">
              <div className="searchable-dropdown" onBlur={() => setIsIngredientDropdownOpen(false)}>
                <input
                  type="text"
                  placeholder="Filter by ingredients..."
                  value={ingredientSearchTerm}
                  onChange={e => {
                    setIngredientSearchTerm(e.target.value);
                    setIsIngredientDropdownOpen(true);
                  }}
                  onFocus={() => setIsIngredientDropdownOpen(true)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && filteredIngredients.length > 0) {
                      handleIngredientSelect(filteredIngredients[0].id);
                    }
                  }}
                />
                {isIngredientDropdownOpen && (
                  <ul className="dropdown-list">
                    {filteredIngredients.map(ingredient => {
                      const activeFilter = selectedIngredientIds.find(item => (typeof item === 'object' ? item.id : item) === ingredient.id);
                      const currentOp = activeFilter && typeof activeFilter === 'object' ? activeFilter.operation : (activeFilter ? 'OR' : null);

                      return (
                        <li key={ingredient.id} className="dropdown-item-with-actions">
                          <span onMouseDown={() => handleIngredientSelect(ingredient.id)} style={{ flexGrow: 1 }}>{ingredient.name}</span>
                          <div className="dropdown-item-actions">
                            <button onMouseDown={(e) => { e.stopPropagation(); handleIngredientSelect(ingredient.id, 'AND'); }}
                              className={`icon-button small ${currentOp === 'AND' ? 'active-green' : ''}`} title="AND">
                              <i className="material-icons">add_circle</i>
                            </button>
                            <button onMouseDown={(e) => { e.stopPropagation(); handleIngredientSelect(ingredient.id, 'OR'); }}
                              className={`icon-button small ${currentOp === 'OR' ? 'active-blue' : ''}`} title="OR">
                              <i className="material-icons">call_split</i>
                            </button>
                            <button onMouseDown={(e) => { e.stopPropagation(); handleIngredientSelect(ingredient.id, 'NOT'); }}
                              className={`icon-button small ${currentOp === 'NOT' ? 'active-red' : ''}`} title="NOT">
                              <i className="material-icons">remove_circle</i>
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              <div className="selected-items">
                {selectedIngredientIds.map(item => {
                  const id = typeof item === 'object' && item !== null ? item.id : item;
                  const operation = typeof item === 'object' && item !== null ? item.operation : 'OR';
                  const ingredient = ingredients.find(i => i.id === id);
                  if (!ingredient) {
                    // Fallback/Safety check
                    return null;
                  }
                  return (
                    <div key={id} className="selected-item" style={{ backgroundColor: getOperationColor(operation) }}>
                      <span style={{ fontWeight: 'bold', marginRight: '5px' }}>{operation}:</span>
                      {ingredient.name}
                      <button title="Remove ingredient" onClick={() => handleRemoveIngredient(id)} className="remove-item-button"><i className="material-icons">close</i></button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button title="Apply filter" onClick={handleApply}>Apply</button>
          <button title="Cancel" onClick={onClose} className="cancel-button">Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;